/**
 * Student Login Page - התחברות תלמידים
 * A dedicated, course-branded login for students taking the finance-literacy
 * course. Uses the shared POST /auth/login; routes students to /learn.
 * Hebrew (RTL) + WCAG 2.1 AA — inherits the global SkipLink + AccessibilityMenu.
 */

import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { LogIn, AlertCircle, GraduationCap, TrendingUp, PieChart, Coins, LineChart } from 'lucide-react'
import api, { handleApiError } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { AuthResponse } from '../types'

const loginSchema = z.object({
  email: z.string().email('כתובת אימייל לא תקינה').min(1, 'שדה חובה'),
  password: z.string().min(1, 'שדה חובה'),
})

type LoginFormData = z.infer<typeof loginSchema>

// Route by role: students → learning tool; section admins → the panel;
// staff → chat; anyone else → dashboard.
const homeRouteFor = (u?: { role?: string } | null) => {
  if (u?.role === 'student') return '/learn'
  if (u?.role === 'sub_admin') return '/admin'
  if (u?.role === 'admin' || u?.role === 'advisor') return '/chat'
  return '/dashboard'
}

const LEARN_ITEMS = [
  { icon: PieChart, text: 'פיזור סיכונים — למה לא לשים את כל הביצים בסל אחד' },
  { icon: TrendingUp, text: 'תשואה מול סיכון — הקשר הבסיסי בעולם ההשקעות' },
  { icon: Coins, text: 'ריבית דריבית — כוח הזמן והתחלה מוקדמת' },
  { icon: LineChart, text: 'מניות, אג"ח וקרנות מדד — מה ההבדל ומתי משתמשים בכל אחד' },
]

export default function StudentLoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionExpired = searchParams.get('expired') === '1'
  const { login } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mfaNotice, setMfaNotice] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setError(null)
    setMfaNotice(false)
    try {
      const response = await api.post<AuthResponse>('/auth/login', data)
      const { user, tokens } = response.data

      if (tokens?.accessToken) {
        login(user, tokens.accessToken)
        navigate(homeRouteFor(user))
      } else if (response.data.requiresMFA) {
        // Staff account used the student page — send them to the main login (which
        // handles the email-2FA flow). Students never have MFA.
        setMfaNotice(true)
      }
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" dir="rtl">
      {/* ── Hero panel (course branding) ── */}
      <section
        className="lg:w-1/2 bg-gradient-to-bl from-primary-700 to-primary-900 text-white px-8 py-12 lg:px-14 lg:py-0 flex items-center"
        aria-labelledby="student-hero-heading"
      >
        <div className="max-w-lg mx-auto">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/15 rounded-2xl mb-6">
            <GraduationCap size={34} aria-hidden="true" />
          </div>
          <h1 id="student-hero-heading" className="text-3xl lg:text-4xl font-heading font-bold mb-4 leading-tight">
            קורס הכרת עולם הפיננסים
          </h1>
          <p className="text-primary-100 text-lg mb-8 leading-relaxed">
            מדריך לימודי חכם שילווה אתכם בקורס — יסביר, ימחיש וייתן דוגמאות כדי שתבינו איך עובד עולם ההשקעות.
            כלי חינוכי בלבד, לא ייעוץ אישי.
          </p>

          <ul className="space-y-4">
            {LEARN_ITEMS.map(({ icon: Icon, text }, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
                  <Icon size={18} aria-hidden="true" />
                </span>
                <span className="text-primary-50 pt-1.5">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Login card ── */}
      <section className="lg:w-1/2 flex items-center justify-center px-4 py-12 bg-gradient-to-bl from-primary-50 to-white">
        <div className="w-full max-w-md">
          <div className="card">
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-1">התחברות תלמידים</h2>
            <p className="text-sm text-gray-500 mb-6">התחברו כדי להמשיך ללמוד עם המדריך הלימודי.</p>

            {sessionExpired && (
              <div className="mb-6 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800" role="status">
                <AlertCircle size={18} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
                <span>תוקף החיבור פג. אנא התחברו מחדש.</span>
              </div>
            )}

            {error && (
              <div className="alert alert-error mb-6" role="alert">
                <AlertCircle size={20} aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            {mfaNotice && (
              <div className="mb-6 flex items-start gap-2 bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 text-sm text-primary-800" role="alert">
                <AlertCircle size={18} className="text-primary-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  חשבון זה אינו חשבון תלמיד. אנא התחברו דרך{' '}
                  <Link to="/login" className="underline font-medium">עמוד ההתחברות הראשי</Link>.
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  אימייל
                </label>
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  className={`input ${errors.email ? 'input-error' : ''}`}
                  placeholder="example@email.com"
                  autoComplete="email"
                  dir="ltr"
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" role="alert" className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  סיסמה
                </label>
                <input
                  {...register('password')}
                  type="password"
                  id="password"
                  className={`input ${errors.password ? 'input-error' : ''}`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  aria-required="true"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                {errors.password && (
                  <p id="password-error" role="alert" className="text-sm text-red-600 mt-1">{errors.password.message}</p>
                )}
              </div>

              <div className="text-left">
                <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                  שכחת סיסמה?
                </Link>
              </div>

              <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="spinner" />
                    <span>מתחבר...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <LogIn size={20} aria-hidden="true" />
                    <span>התחברות</span>
                  </div>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                עדיין אין לך חשבון?{' '}
                <Link to="/register-student" className="text-primary-600 hover:text-primary-700 font-medium">
                  הצטרפו עם קוד כיתה
                </Link>
              </p>
            </div>
          </div>

          <div className="text-center mt-6 text-sm text-gray-500">
            <p>יפתח ונגר יועץ השקעות — כלי חינוכי לתלמידים</p>
            <p className="mt-1">© 2026 כל הזכויות שמורות</p>
          </div>
        </div>
      </section>
    </div>
  )
}
